import type {
  BulkPhotoMatch,
  BulkPhotoStatus,
  BulkPhotoTarget,
} from "./types/bulk-checkins";

// Bulk photo check-ins (spec 39 / ATL-166): the review list both clients show
// after matching. One row per target per Europe/London day (a visit). Several
// photos of one summit on one day collapse into a row; the default photo is
// the one closest to the point, earliest on a tie, and only that one uploads.

/** Photos per match request; must equal BULK_CHECKIN_MAX_PHOTOS in @atlas/db. */
export const BULK_CHECKIN_MATCH_CHUNK = 200;

export interface BulkRowPhoto {
  ref: string;
  distanceM: number;
  takenAt: string;
  /** What this photo posts to: one hill's photos can differ (a pillar photo
   * posts to the pillar, a summit photo to the peak). */
  target: BulkPhotoTarget;
}

export interface BulkReviewRow {
  /** summit (or landmark) + visit day. Stable whichever photo is chosen. */
  key: string;
  /** The default photo's target. Post to rowTarget(row, chosenRef). */
  target: BulkPhotoTarget;
  peak: BulkPhotoMatch["peak"];
  visitDay: string;
  /** Closest first. */
  photos: BulkRowPhoto[];
  /** The photo that uploads. */
  chosenRef: string;
  peakLocked: boolean;
  alreadyCollected: boolean;
  alreadyCheckedIn: boolean;
}

export interface BulkUnmatchedPhoto {
  ref: string;
  status: Exclude<BulkPhotoStatus, "matched" | "history_locked">;
}

export interface BulkReview {
  /** Can be checked in, newest day first. */
  rows: BulkReviewRow[];
  /** Summits outside the free plan's window (grouped the same way). */
  locked: BulkReviewRow[];
  unmatched: BulkUnmatchedPhoto[];
}

export function buildBulkReview(
  matches: BulkPhotoMatch[],
  takenAtByRef: ReadonlyMap<string, string | null>,
): BulkReview {
  const groups = new Map<string, BulkReviewRow>();
  const lockedGroups = new Map<string, BulkReviewRow>();
  const unmatched: BulkUnmatchedPhoto[] = [];

  for (const m of matches) {
    if ((m.status !== "matched" && m.status !== "history_locked") || !m.target || !m.visitDay) {
      unmatched.push({
        ref: m.ref,
        status:
          m.status === "matched" || m.status === "history_locked" ? "no_match" : m.status,
      });
      continue;
    }
    const into = m.status === "matched" ? groups : lockedGroups;
    // One visit per hill per day, whether a photo matched the summit or its pillar.
    const key = m.peak
      ? `peak:${m.peak.id}:${m.visitDay}`
      : `${m.target.kind}:${m.target.id}:${m.visitDay}`;
    const photo: BulkRowPhoto = {
      ref: m.ref,
      distanceM: m.target.distanceM,
      takenAt: takenAtByRef.get(m.ref) ?? "",
      target: m.target,
    };
    const row = into.get(key);
    if (row) {
      row.photos.push(photo);
      row.alreadyCheckedIn ||= m.alreadyCheckedIn;
      row.alreadyCollected ||= m.alreadyCollected;
    } else {
      into.set(key, {
        key,
        target: m.target,
        peak: m.peak,
        visitDay: m.visitDay,
        photos: [photo],
        chosenRef: m.ref,
        peakLocked: m.peakLocked,
        alreadyCollected: m.alreadyCollected,
        alreadyCheckedIn: m.alreadyCheckedIn,
      });
    }
  }

  const finish = (rows: BulkReviewRow[]) => {
    for (const row of rows) {
      row.photos.sort(
        (a, b) => a.distanceM - b.distanceM || a.takenAt.localeCompare(b.takenAt),
      );
      row.chosenRef = row.photos[0]!.ref;
      row.target = row.photos[0]!.target;
    }
    return rows.sort(
      (a, b) => b.visitDay.localeCompare(a.visitDay) || a.target.name.localeCompare(b.target.name),
    );
  };

  return {
    rows: finish([...groups.values()]),
    locked: finish([...lockedGroups.values()]),
    unmatched,
  };
}

/** Where the row's chosen photo posts. */
export function rowTarget(row: BulkReviewRow, chosenRef: string): BulkPhotoTarget {
  return row.photos.find((p) => p.ref === chosenRef)?.target ?? row.target;
}

/** Rows ticked by default: everything except a check-in already made that day. */
export function defaultBulkSelection(rows: BulkReviewRow[]): Set<string> {
  return new Set(rows.filter((r) => !r.alreadyCheckedIn).map((r) => r.key));
}

/** Why a photo didn't match, in review-screen wording. */
export function bulkUnmatchedReason(status: BulkUnmatchedPhoto["status"]): string {
  switch (status) {
    case "no_location":
      return "No location in this photo";
    case "no_date":
      return "No date in this photo";
    default:
      return "No summit within 75 m";
  }
}

/**
 * Deterministic check-in id for a bulk row, so re-running the same batch is
 * deduped by the server's (user, clientId) index instead of adding a second
 * check-in. UUID-shaped (the server only checks the shape); not a security
 * value.
 */
export function bulkCheckinClientId(userId: string, row: Pick<BulkReviewRow, "key">): string {
  const input = `atlas-bulk|${userId}|${row.key}`;
  // cyrb128: four 32-bit lanes.
  let h1 = 1779033703,
    h2 = 3144134277,
    h3 = 1013904242,
    h4 = 2773480762;
  for (let i = 0; i < input.length; i++) {
    const k = input.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;
  const hex = [h1, h2, h3, h4].map((h) => (h >>> 0).toString(16).padStart(8, "0")).join("");
  // Version 4 / variant bits so it reads as an ordinary UUID.
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `4${hex.slice(13, 16)}`,
    `${((parseInt(hex[16]!, 16) & 0x3) | 0x8).toString(16)}${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join("-");
}

/** "23 hills from 61 photos" style summary counts. */
export function bulkSummary(rows: BulkReviewRow[], selected: ReadonlySet<string>) {
  const chosen = rows.filter((r) => selected.has(r.key));
  const credits = (r: BulkReviewRow) => r.peak !== null && !r.peakLocked;
  const hills = new Set(chosen.filter(credits).map((r) => r.peak!.id));
  const newHills = new Set(
    chosen.filter((r) => credits(r) && !r.alreadyCollected).map((r) => r.peak!.id),
  );
  const places = new Set(chosen.filter((r) => !credits(r)).map((r) => r.target.id));
  return { checkins: chosen.length, hills: hills.size, newHills: newHills.size, places: places.size };
}
